import React from "react";
import { useNavigate } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";

import { Container, Row, Col, Card, CardHeader, Button } from "@/Components/ui";
import { dialogService } from "@/utils/dialogService";

import BreadCrumb from "../../Components/Common/BreadCrumb";
import { useAuthContext } from "../../Components/AuthContext";
import { SUBDOMAIN } from "../../assets/meta/meta";
import { membershipService } from "../../api/services/membership.service";
import { MemberInvitation, MemberInvitationStatus } from "../../api/types/member";
import {
  ensureCurrentPermissionOption,
  getPermissionOptions,
  permissionLabelMap,
} from "./memberPermissions";

const statusLabels: Record<string, string> = {
  all: "전체",
  pending: "대기",
  accepted: "수락",
  canceled: "취소",
  expired: "만료",
};

const statusOrder: Array<"all" | MemberInvitationStatus> = [
  "all",
  "pending",
  "accepted",
  "canceled",
  "expired",
];

const canManageMemberMenu = (userInfo: any, requiredPermission: number = 1) => {
  if (!userInfo?.site_permission) return false;
  const permission = Number(userInfo.site_permission.permission);
  if (!Number.isFinite(permission) || permission < requiredPermission) return false;
  if (permission === 2) return true;
  return userInfo.site_permission.subdomain === SUBDOMAIN;
};

const formatDate = (dateText: string | null) => {
  if (!dateText) return "-";
  return new Date(dateText).toLocaleString("ko-KR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const MemberInvitationListPage = () => {
  const navigate = useNavigate();
  const { userInfo } = useAuthContext();
  const sitePermission = userInfo?.site_permission;
  const actorPermission = Number(sitePermission?.permission || 0);
  const canManage = canManageMemberMenu(userInfo, 1);

  const [invitations, setInvitations] = React.useState<MemberInvitation[]>([]);
  const [filterStatus, setFilterStatus] = React.useState<"all" | MemberInvitationStatus>("all");
  const [isLoading, setIsLoading] = React.useState(false);

  const permissionOptions = React.useMemo(
    () => getPermissionOptions(actorPermission),
    [actorPermission]
  );

  const fetchInvitations = React.useCallback(async () => {
    if (!sitePermission?.site) return;
    setIsLoading(true);
    try {
      const result = await membershipService.getMemberInvitations(
        sitePermission.site,
        filterStatus === "all" ? undefined : { status: filterStatus }
      );
      setInvitations(result.data || []);
    } catch (error) {
      console.log(error);
      toast.error("초대 목록을 불러오지 못했습니다.");
      setInvitations([]);
    } finally {
      setIsLoading(false);
    }
  }, [filterStatus, sitePermission?.site]);

  React.useEffect(() => {
    fetchInvitations();
  }, [fetchInvitations]);

  const onResend = async (invitation: MemberInvitation) => {
    if (!sitePermission?.site) return;
    try {
      await membershipService.resendMemberInvitation(sitePermission.site, invitation.id);
      toast.success("재발송했습니다.");
      await fetchInvitations();
    } catch (error) {
      console.log(error);
      toast.error("재발송에 실패했습니다.");
    }
  };

  const onCancel = async (invitation: MemberInvitation) => {
    if (!sitePermission?.site) return;
    const confirmed = await dialogService.confirm("이 초대를 취소하시겠습니까?");
    if (!confirmed) return;

    try {
      await membershipService.cancelMemberInvitation(sitePermission.site, invitation.id);
      toast.success("취소했습니다.");
      await fetchInvitations();
    } catch (error) {
      console.log(error);
      toast.error("취소에 실패했습니다.");
    }
  };

  const onPermissionUpdate = async (invitation: MemberInvitation, permission: number) => {
    if (!sitePermission?.site || !invitation.user?.id) return;
    if (invitation.user.id === userInfo?.id) {
      toast.error("본인 권한은 직접 변경할 수 없습니다.");
      return;
    }

    try {
      await membershipService.updateMemberPermission(sitePermission.site, invitation.user.id, {
        permission,
      });
      toast.success("권한을 변경했습니다.");
      await fetchInvitations();
    } catch (error) {
      console.log(error);
      toast.error("권한 변경에 실패했습니다.");
    }
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="초대 관리" pageTitle="멤버" />
          <Row>
            <Col lg={12}>
              <Card>
                <CardHeader className="border-0">
                  <Row className="g-3 align-items-center">
                    <div className="col-sm">
                      <h5 className="card-title mb-0">초대 목록</h5>
                    </div>
                    <div className="col-sm-auto d-flex gap-2">
                      <Button color="soft-primary" onClick={() => navigate("/member-invite")}>
                        비회원 초대
                      </Button>
                    </div>
                  </Row>
                  <Row className="mt-3">
                    <div className="col-sm-12">
                      {statusOrder.map((status) => (
                        <Button
                          key={status}
                          className={`me-2 ${filterStatus === status ? "btn-primary" : "btn-soft-primary"}`}
                          color={filterStatus === status ? "primary" : "soft-primary"}
                          size="sm"
                          onClick={() => setFilterStatus(status)}
                        >
                          {statusLabels[status]}
                        </Button>
                      ))}
                    </div>
                  </Row>
                </CardHeader>

                <div className="card-body pt-0">
                  {isLoading ? (
                    <p className="py-3">불러오는 중...</p>
                  ) : invitations.length === 0 ? (
                    <p className="py-3 mb-0">표시할 초대가 없습니다.</p>
                  ) : (
                    <div className="w-full overflow-x-auto">
                      <table className="table table-hover table-bordered">
                        <thead className="bg-slate-50">
                          <tr>
                            <th>이메일</th>
                            <th>권한</th>
                            <th>상태</th>
                            <th>초대자</th>
                            <th>만료일</th>
                            <th>변경일</th>
                            <th>작업</th>
                          </tr>
                        </thead>
                        <tbody>
                          {invitations.map((invitation) => (
                            <tr key={invitation.id}>
                              <td>{invitation.email}</td>
                              <td>
                                <div className="d-flex align-items-center gap-2">
                                  <span>
                                    {invitation.permission_label ||
                                      permissionLabelMap[invitation.permission] ||
                                      "-"}
                                  </span>
                                  {canManage && invitation.status === "accepted" && invitation.user ? (
                                    <select
                                      className="form-select form-select-sm"
                                      value={invitation.permission}
                                      onChange={(event) =>
                                        onPermissionUpdate(invitation, Number(event.target.value))
                                      }
                                    >
                                      {ensureCurrentPermissionOption(
                                        permissionOptions,
                                        invitation.permission
                                      ).map((option) => (
                                        <option key={option.value} value={option.value}>
                                          {option.label}
                                        </option>
                                      ))}
                                    </select>
                                  ) : null}
                                </div>
                              </td>
                              <td>{invitation.status_label || invitation.status}</td>
                              <td>
                                {invitation.invited_by_user
                                  ? invitation.invited_by_user.username ||
                                    invitation.invited_by_user.phone_num
                                  : "-"}
                              </td>
                              <td>{formatDate(invitation.expires_at)}</td>
                              <td>
                                {invitation.accepted_at
                                  ? formatDate(invitation.accepted_at)
                                  : invitation.recalled_at
                                    ? formatDate(invitation.recalled_at)
                                    : "-"}
                              </td>
                              <td>
                                {canManage ? (
                                  <div className="d-flex gap-2">
                                    {invitation.status === "pending" ? (
                                      <>
                                        <Button
                                          size="sm"
                                          color="soft-primary"
                                          onClick={() => onResend(invitation)}
                                        >
                                          재발송
                                        </Button>
                                        <Button
                                          size="sm"
                                          color="soft-danger"
                                          onClick={() => onCancel(invitation)}
                                        >
                                          취소
                                        </Button>
                                      </>
                                    ) : null}
                                  </div>
                                ) : null}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
      <Toaster position="top-center" />
    </React.Fragment>
  );
};

export default MemberInvitationListPage;
